import {omit} from 'lodash';

import {PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  AnalyticsReportsEnum,
  ReportFilterOptionsEnum,
} from '../../../../analytics/helpers/analyticsReportHelpers';
import {AnalyticsMetricsDictionary} from '../../../../models/api/analyticsMetricsModel';
import {AppAction} from '../../../appActions';
import {AnalyticsPanelReportActionsEnum} from './analyticsPanelReportActions';
import {AnalyticsPanelReportJob, AnalyticsPanelReportState} from './analyticsPanelReportState';

/*
 * Analytics Report panel.
 */

export function analyticsPanelReportReducer(
  state: AnalyticsPanelReportState,
  action: AppAction,
): AnalyticsPanelReportState {
  switch (action.type) {
    // When setting a new report type, reset everything but `metrics`.
    case AnalyticsPanelReportActionsEnum.SET_REPORT_TYPE:
      return {
        ...state,
        type: action.payload,
        jobs: [],
      };

    case AnalyticsPanelReportActionsEnum.RESET_JOBS:
      return {
        ...state,
        jobs: [],
      };

    case AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS: {
      const newJob: AnalyticsPanelReportJob = {
        jobUid: undefined, // We don't have a job uid yet.
        isLoading: true,
        progress: 0,
        hasStarted: false,
        error: undefined,
        ...action.payload,
      };

      // Remove existing metrics from the state.
      // We need to cast here because `omit` is not computing the type correctly.
      const filteredMetrics = omit(state.metrics, action.payload.metrics) as AnalyticsMetricsDictionary;

      return {
        ...state,
        jobs: updateOrAddJob(
          newJob,
          state.jobs,
          () => false, // This will always create a new job when we fetch new metrics.
          (job) => ({...job, ...newJob}),
        ),
        metrics: filteredMetrics,
      };
    }

    case AnalyticsPanelReportActionsEnum.UPDATE_ANALYTICS_JOBS: {
      // We need to update the corresponding job.
      // Note: the related job won't have the uid yet on first update. For this case we use the local id.
      // We also check that the payload progress is not smaller than the current progress, in case of event being in the wrong order.
      const {jobUid, jobLocalId, progress} = action.payload;
      const updatedJobs = updateJobs(
        state.jobs,
        (job) => (job.jobUid === jobUid || job.localId === jobLocalId) && progress >= job.progress,
        (job) => ({
          ...job,
          jobUid: action.payload.jobUid,
          isLoading: true,
          hasStarted: true,
          progress: action.payload.progress,
          startTime: action.payload.startTime,
        }),
      );

      return {
        ...state,
        jobs: updatedJobs,
      };
    }

    case AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_COMPLETED: {
      const metricsDictionary = action.payload.metrics.reduce<Partial<AnalyticsMetricsDictionary>>(
        (acc, curr) => ({
          ...acc,
          [curr.id]: curr,
        }),
        {},
      );

      // We need to update the corresponding job because we don't want to stay connected to the related Pusher room.
      // Note: the related job may not have a jobUid (if the metrics were computed synchronously). For this case we use the local id.
      const updatedJobs = updateJobs(
        state.jobs,
        (job) => job.jobUid === action.payload.uid || job.localId === action.payload.jobLocalId,
        (job) => ({
          ...job,
          jobUid: undefined,
          isLoading: false,
          hasStarted: true,
          progress: action.payload.progress,
          endTime: action.payload.endTime,
        }),
      );

      return {
        ...state,
        jobs: updatedJobs,
        metrics: {
          ...state.metrics,
          ...metricsDictionary,
        },
        timedOutMetrics: action.payload.timedOutMetrics,
      };
    }

    case AnalyticsPanelReportActionsEnum.FETCH_ANALYTICS_METRICS_FAILED: {
      // We need to update the corresponding job because we don't want to stay connected to the related Pusher room.
      // Note: the related job may not have a jobUid (if an error occured while retrieving data from the BE). For this case we use the local id.
      const updatedJobs = updateJobs(
        state.jobs,
        (job) => job.jobUid === action.payload.jobUid || job.localId === action.payload.jobLocalId,
        (job) => ({
          ...job,
          jobUid: undefined,
          isLoading: false,
          error: action.payload.error,
        }),
      );

      return {
        ...state,
        jobs: updatedJobs,
      };
    }

    case AnalyticsPanelReportActionsEnum.UPDATE_SELECTED_METRICS: {
      return {
        ...state,
        options: {
          ...state.options,
          [ReportFilterOptionsEnum.SELECTED_METRICS]: action.payload,
        },
      };
    }

    case AnalyticsPanelReportActionsEnum.UPDATE_BUCKET_BOUNDARIES: {
      return {
        ...state,
        options: {
          ...state.options,
          [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: action.payload,
        },
      };
    }

    case AnalyticsPanelReportActionsEnum.SET_REPORT_OPTIONS: {
      return {
        ...state,
        options: action.payload,
      };
    }

    default:
      return state;
  }
}

export function makeAnalyticsPanelReportState(
  type = AnalyticsReportsEnum.OVERVIEW,
): AnalyticsPanelReportState {
  return {
    type,
    jobs: [],
    metrics: {} as AnalyticsMetricsDictionary,
    timedOutMetrics: [],
    options: {},
  };
}

/*
 * Helpers.
 */

function updateJobs(
  jobs: ReadonlyArray<AnalyticsPanelReportJob>,
  predicate: PredicateOf<AnalyticsPanelReportJob>,
  updater: (job: AnalyticsPanelReportJob) => AnalyticsPanelReportJob,
) {
  return jobs.map((job) => {
    if (!predicate(job)) {
      return job;
    }

    return updater(job);
  });
}

function updateOrAddJob(
  newJob: AnalyticsPanelReportJob,
  jobs: ReadonlyArray<AnalyticsPanelReportJob>,
  predicate: PredicateOf<AnalyticsPanelReportJob>,
  updater: (job: AnalyticsPanelReportJob) => AnalyticsPanelReportJob,
) {
  let updated = false;
  const newJobs = updateJobs(
    jobs,
    (job) => {
      if (!predicate(job)) {
        return false;
      }
      updated = true;
      return true;
    },
    updater,
  );

  return updated ? newJobs : [...newJobs, newJob];
}
